import { formVar, proccesVar, userVar } from "@/store";
import colors from "@/theme/colors";
import styled from "@emotion/styled";
import Image from "next/image";
import React from "react";

/* Grid with two rows sharing the same columns:
   [circle][connector][circle][connector]...[circle] */
const ProgressGrid = styled.div`
  --circle: 46px;
  --line-h: 6px;

  width: 100%;
  max-width: 820px;
  padding: 0 16px;
  margin-top: 36px;
  direction: rtl; /* Hebrew */
  display: grid;
  align-items: start;        /* row alignment */
  justify-items: stretch;    /* columns fill width */
  row-gap: 8px;              /* space between circles row and labels row */

  @media (max-width: 575px) {
    --circle: 40px;
    --line-h: 5px;
  }
`;

/* Row 1: circles + connectors */
const CirclesRow = styled.div`
  grid-row: 1 / 2;
  display: contents; /* children participate directly in parent grid */
`;

/* Row 2: labels (exactly under every circle) */
const LabelsRow = styled.div`
  grid-row: 2 / 3;
  display: contents;
`;

/* Circle cell occupies a fixed-width column */
const StepNode = styled.div`
  width: var(--circle);
  height: var(--circle);
  display: flex;
  justify-content: center;
  align-items: center;
  justify-self: center; /* center within its fixed column */
`;

/* Circle style */
const StepCircle = styled.div<{ state: "completed" | "active" | "upcoming" }>(
  ({ state }) => ({
    width: "var(--circle)",
    height: "var(--circle)",
    borderRadius: "50%",
    backgroundColor:
      state === "completed" || state === "active" ? colors.black : colors.svgGrey,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: colors.white,
    fontWeight: 700,
    fontSize: "1.2rem",
    zIndex: 2,
    boxShadow: state === "active" ? "0 0 0 3px rgba(0,0,0,0.08)" : "none",
  })
);

/* Connector cell is flexible column between circles, animated gray → black */
const Connector = styled.div<{ completed: boolean }>((p) => ({
  height: "var(--line-h)",
  borderRadius: "calc(var(--line-h) / 2)",
  alignSelf: "center",
  zIndex: 1,

  /* base track + animated fill (RTL → fill from right to left) */
  backgroundColor: colors.svgGrey,
  backgroundImage: `linear-gradient(to left, ${colors.black}, ${colors.black})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: p.completed ? "100% 100%" : "0% 100%",
  transition: "background-size 0.6s ease-in-out",
}));

const Label = styled.p<{ active?: boolean }>(({ active }) => ({
  margin: 0,
  fontSize: 16,            // was 15 → bump
  fontWeight: active ? 700 : 400,
  color: colors.black,
  lineHeight: "22px",      // was 20px → bump a bit
  textAlign: "center",
  justifySelf: "center",
  maxWidth: "10rem",       // give labels a bit more breathing room
  "@media (min-width: 576px)": {
    fontSize: 18,          // desktop bump
    lineHeight: "24px",
  },
}));

/* ===== Component ===== */
interface StepsProps {
  current: number; // 0-based
}

enum StepText {
  PersonalDetails = "פרטים אישיים",
  Default = "בחירת חבילה",
  CreditCard = "כרטיס אשראי",
  Confirmation = "אישור הצטרפות",
}

export const Steps: React.FC<StepsProps> = ({ current }) => {
  const labels = Object.values(StepText);
  const name = `${formVar().firstName} ${formVar().lastName}`.trim();
  const prefix = "חבילת ";
  const packageName =
    proccesVar().selectedPackageName ?? userVar().packageChosen ?? "";

  // Build the shared column template: [circle 1] [connector] [circle 2] ... [circle n]
  const columnsTemplate =
    (Array.from({ length: labels.length - 1 })
      .map(() => "var(--circle) minmax(24px, 0.6fr)") // 👈 tighter: min 24px, grow a bit
      .join(" ")) + " var(--circle)";

  return (
    <ProgressGrid style={{ gridTemplateColumns: columnsTemplate }}>
      <CirclesRow>
        {labels.map((_, i) => {
          const isCompleted = current > i;
          const isActive = current === i;
          const state: "completed" | "active" | "upcoming" =
            isCompleted ? "completed" : isActive ? "active" : "upcoming";
          const isLast = i === labels.length - 1;

          // Grid placement: circles go in columns 1,3,5,... (index*2+1)
          const circleCol = i * 2 + 1;

          return (
            <React.Fragment key={`circle-${i}`}>
              <StepNode style={{ gridColumn: circleCol }}>
                <StepCircle state={state} aria-hidden={false}>
                  {isCompleted ? (
                    <Image src="/success_v.svg" alt="הצלחה" width={18} height={18} />
                  ) : (
                    i + 1
                  )}
                </StepCircle>
              </StepNode>
              {!isLast && (
                <Connector
                  style={{ gridColumn: circleCol + 1 }}
                  completed={current > i}
                />
              )}
            </React.Fragment>
          );
        })}
      </CirclesRow>

      <LabelsRow>
        {labels.map((step, i) => {
          const isCompleted = current > i;
          const isActive = current === i;
          const labelText = (() => {
            if (isCompleted) {
              if (i === 0) return name || step;
              if (i === 1 && packageName) return prefix + packageName;
              return step;
            }
            if (!isCompleted && i === 1 && packageName) return prefix + packageName;
            return step;
          })();

          const circleCol = i * 2 + 1;

          return (
            <Label key={`label-${i}`} active={isActive} style={{ gridColumn: circleCol }}>
              {labelText}
            </Label>
          );
        })}
      </LabelsRow>
    </ProgressGrid>
  );
};
